import pdfMake from 'pdfmake/build/pdfmake';
import {
  HitDropsDTO,
  HitReportDataDTO,
  HitReportIngredientDTO,
} from 'business/models/Models';
import { getHeader, TABLE_LAYOUT } from './ExperimentReportPDF';
import { MIME_TYPES } from 'utils/MimeTypes';
import {
  createPdfWithPopupBlockerHandler,
  getScreenNameColumnsTable,
  HIT_REPORT_EXPERIMENT_LABEL_FONT_SIZE,
  HIT_REPORT_SCREEN_NAME_SHORT_MAX_LENGTH,
  HIT_REPORT_SCREEN_NAME_MEDIUM_MAX_LENGTH,
} from './utils';
import moment from 'moment';
import { fonts } from './ReportsConstants';
import { HitScoreName } from 'components/Experiment/FindDrop/ScoringFilters/constants';
pdfMake.fonts = { Roboto: fonts.Roboto };

type DropData = {
  expName: string;
  dateImaged: Date;
  title: string;
  sampleVol: number;
  wellVol: number;
  proteinName: string;
  scores: string[];
  isAutoScoreIgnored: boolean;
  wellIngs: string[];
  captures: CapturesData[];
};
type CapturesData = {
  id: number;
  img: string;
  ext: string;
};

function getScoreLabel(drop: HitDropsDTO) {
  if (!drop.wellDropScoreName) {
    return '';
  }
  if (
    drop.wellDropScoreName === HitScoreName.PositiveHit &&
    drop.wellDropScoreGroupName
  ) {
    return `${drop.wellDropScoreName} (${drop.wellDropScoreGroupName})`;
  }
  return drop.wellDropScoreName;
}

export type HitsReportOptions = {
  latestInspectionOnly?: boolean;
  uvHitsOnly?: boolean;
};

function getScreenNameColumns(screenNames: string[]) {
  return getScreenNameColumnsTable(
    screenNames,
    'experimentLabelText',
    HIT_REPORT_SCREEN_NAME_SHORT_MAX_LENGTH,
    HIT_REPORT_SCREEN_NAME_MEDIUM_MAX_LENGTH,
  );
}
export function createHitReportPDF(
  reportData: HitReportDataDTO[],
  saveReport: boolean,
  options?: HitsReportOptions,
  onPopupBlocked?: () => void,
) {
  const pdfDefinition = {
    info: {
      title: 'Hits Report',
      author: 'Rock Maker',
      subject: 'Hits Report',
    },
    content: CreateContent(reportData, options),
    styles: {
      tableStyle: { alignment: 'center' },
      tableHeader: { lineHeight: 1, bold: true, fontSize: '12', fillColor: '#D9D9D9' },
      experimentLabelText: {
        fontSize: HIT_REPORT_EXPERIMENT_LABEL_FONT_SIZE,
        lineHeight: 1.5,
      },
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.5,
    },
    pageMargins: [50, 70, 50, 30],
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    'Hits Report',
    saveReport,
    onPopupBlocked,
  );
}
function CreateContent(reportData: HitReportDataDTO[], options?: HitsReportOptions) {
  const content = [];
  content.push({ text: 'Hits Report', fontSize: '28', alignment: 'center' });
  content.push({ text: 'Hits By Ingredients', fontSize: '20', bold: true });
  [
    options?.latestInspectionOnly && 'Hits from latest inspection only',
    options?.uvHitsOnly && 'UV Hits only',
  ]
    .filter(Boolean)
    .forEach((text) => {
      content.push({
        text,
        fontSize: '14',
        bold: false,
      });
    });

  printHitIngredientsTable(content, reportData);

  reportData.forEach((exp) => {
    const experimentNamesDict: { [key: number]: string } = {};
    const plateImagedDateDict: { [key: number]: string } = {};
    const captureProfilesDict: { [key: number]: string } = {};
    const plateBarcodeDict: { [key: number]: string } = {};
    const dropsReport: {
      [key: number]: { [key: number]: { [key: number]: { [key: number]: DropData } } };
    } = {};

    for (let i = 0; i < exp.dropsData.length; i++) {
      const drop = exp.dropsData[i];

      if (!dropsReport[drop.experimentId]) {
        dropsReport[drop.experimentId] = {};
      }
      if (!dropsReport[drop.experimentId][drop.plateId]) {
        dropsReport[drop.experimentId][drop.plateId] = {};
      }
      if (!dropsReport[drop.experimentId][drop.plateId][drop.inspectionId]) {
        dropsReport[drop.experimentId][drop.plateId][drop.inspectionId] = {};
      }
      const scoreLabel = getScoreLabel(drop);
      if (
        !dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][drop.wellDropId]
      ) {
        dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][drop.wellDropId] =
          {
            title: drop.wellName + '-' + drop.dropNumber,
            sampleVol: drop.sampleVolume,
            wellVol: drop.dropVolume,
            scores: scoreLabel ? [scoreLabel] : [],
            isAutoScoreIgnored: drop.isAutoScoreIgnored === true,
            wellIngs: drop.wellIngs,
            proteinName: drop.proteinName,
            captures: [
              {
                id: drop.captureProfileId,
                img: drop.imageData,
                ext: drop.imageExt,
              } as CapturesData,
            ],
          } as DropData;
      } else {
        const existingDrop =
          dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][
            drop.wellDropId
          ];
        if (scoreLabel && !existingDrop.scores.includes(scoreLabel)) {
          existingDrop.scores.push(scoreLabel);
        }
        if (drop.isAutoScoreIgnored === true) {
          existingDrop.isAutoScoreIgnored = true;
        }
        if (existingDrop.captures.findIndex((c) => c.id == drop.captureProfileId) < 0) {
          existingDrop.captures.push({
            id: drop.captureProfileId,
            img: drop.imageData,
            ext: drop.imageExt,
          });
        }
      }
      if (!experimentNamesDict[drop.experimentId]) {
        experimentNamesDict[drop.experimentId] = drop.expName;
      }
      const key = `${drop.plateId}_${drop.inspectionId}`;
      if (!plateImagedDateDict[key]) {
        //plateImagedDateDict[drop.plateId] = new Date(drop.dateImaged).toLocaleDateString('en-US');
        plateImagedDateDict[key] = moment(drop.dateImaged).format('YYYY-MM-DD hh:mm a');
      }
      if (!captureProfilesDict[drop.captureProfileId]) {
        captureProfilesDict[drop.captureProfileId] = drop.captureProfileName;
      }
      if (!plateBarcodeDict[drop.plateId]) {
        plateBarcodeDict[drop.plateId] = drop.plateBarcode;
      }
    }

    getHItsByExperimentTable(
      dropsReport,
      content,
      experimentNamesDict,
      plateImagedDateDict,
      captureProfilesDict,
      plateBarcodeDict,
      exp.screenNames,
    );
  });
  return content;
}

function printHitIngredientsTable(content: any[], reportData: HitReportDataDTO[]) {
  const ingredients: any[] = [];

  reportData.forEach((exp) => {
    ingredients.push(...exp.ingredientsData);
  });

  const mergedIngs: { [key: number]: HitReportIngredientDTO } = {};
  ingredients.forEach((ing) => {
    if (mergedIngs[ing.id]) {
      mergedIngs[ing.id].ingredientCount += ing.ingredientCount;
      if (mergedIngs[ing.id].highConcentration < ing.highConcentration) {
        mergedIngs[ing.id].highConcentration = ing.highConcentration;
      }
      if (mergedIngs[ing.id].lowConcentration > ing.lowConcentration) {
        mergedIngs[ing.id].lowConcentration = ing.lowConcentration;
      }
      if (mergedIngs[ing.id].highPh < ing.highPh) {
        mergedIngs[ing.id].highPh = ing.highPh;
      }
      if (mergedIngs[ing.id].lowPh > ing.lowPh) {
        mergedIngs[ing.id].lowPh = ing.lowPh;
      }
    } else {
      mergedIngs[ing.id] = ing;
    }
  });
  content.push(getHitIngredientsTable(mergedIngs));
}

function getHItsByExperimentTable(
  dropsData: {
    [key: number]: { [key: number]: { [key: number]: { [key: number]: DropData } } };
  },
  content: any[],
  expNames,
  imagedDates,
  captureProfiles,
  plateBarcodes,
  screenNames?: string[],
) {
  Object.keys(dropsData).forEach((expId) => {
    content.push({
      text: 'HITS BY EXPERIMENTS',
      fontSize: 15,
      bold: true,
      pageBreak: 'before',
    });
    content.push({
      text: [{ text: 'Experiment Name: ', bold: true }, { text: expNames[expId] }],
      style: 'experimentLabelText',
    });
    let plateCount = 1;
    Object.keys(dropsData[expId]).forEach((plateId) => {
      const plateDateImaged =
        imagedDates[
          Object.keys(imagedDates)
            .filter((k) => k.split('_')[0] === String(plateId))
            .sort((a, b) => Number(b.split('_')[1]) - Number(a.split('_')[1]))[0]
        ];
      const plateBarcode = plateBarcodes[plateId];
      content.push({
        text: [
          { text: 'Barcode (ID): ', bold: true },
          { text: `${plateBarcode} (${plateId})` },
        ],
        style: 'experimentLabelText',
      });
      content.push({
        text: [{ text: 'Imaged: ', bold: true }, { text: plateDateImaged }],
        style: 'experimentLabelText',
      });
      content.push(getScreenNameColumns(screenNames ?? []));

      const tableBody = [];
      const headerRow = [{ text: 'Drop Info', width: '50%', style: 'tableHeader' }];
      Object.keys(captureProfiles).forEach((cp) =>
        headerRow.push({ text: captureProfiles[cp], width: '25%', style: 'tableHeader' }),
      );
      tableBody.push(headerRow);

      Object.keys(dropsData[expId][plateId])
        .sort((a, b) => parseInt(b) - parseInt(a))
        .forEach((inspectionId) => {
          Object.keys(dropsData[expId][plateId][inspectionId]).forEach((dropId) => {
            const dateImaged = imagedDates[`${plateId}_${inspectionId}`];
            const drop: DropData = dropsData[expId][plateId][inspectionId][dropId];
            const tableRow: any[] = [
              {
                stack: [
                  { text: drop.title, style: 'header2', margin: [0, 5, 0, 5] },
                  {
                    text: [{ text: 'Sample: ', bold: true }, { text: drop.proteinName }],
                  },
                  {
                    text: [
                      { text: 'Sample / Well Volume: ', bold: true },
                      drop.sampleVol + 'uL' + ' / ' + drop.wellVol + 'uL',
                    ],
                  },
                  {
                    text: [
                      { text: 'Scores: ', bold: true },
                      drop.scores.join(', '),
                      drop.isAutoScoreIgnored ? ' (Ignored)' : '',
                    ],
                  },
                  drop.wellIngs ? drop.wellIngs.map((x) => x) : '',
                ],
              },
            ];

            Object.keys(captureProfiles).forEach((cp) => {
              const dropCapture = drop.captures.find((d) => d.id === Number(cp));
              if (dropCapture && dropCapture?.img) {
                tableRow.push({
                  stack: [
                    {
                      image:
                        'data:' +
                        MIME_TYPES[dropCapture.ext] +
                        ';base64,' +
                        dropCapture.img,
                      height: 130,
                      width: 160,
                    },
                    {
                      text: `*${dateImaged}`,
                      alignment: 'center',
                    },
                  ],
                });
              } else {
                tableRow.push({
                  text: 'No Image Found',
                  fontSize: 18,
                  alignment: 'center',
                  margin: [0, 55, 0, 0],
                });
              }
            });

            tableBody.push(tableRow);
          });
        });
      let pgBreak = 'after';
      if (plateCount == Object.keys(dropsData[expId]).length) {
        pgBreak = '';
      }
      content.push({
        table: { body: tableBody, headerRows: 1, dontBreakRows: true },
        pageBreak: pgBreak,
      });
      plateCount++;
    });
  });
}

function getHitIngredientsTable(ingredientData: {
  [key: number]: HitReportIngredientDTO;
}) {
  const body: any = [
    [
      { text: 'Ingredient', bold: true, alignment: 'left', style: 'tableHeader' },
      { text: 'No. of Hits', bold: true, style: 'tableHeader' },
      { text: 'pH Range', bold: true, style: 'tableHeader' },
      { text: 'Concentration Range', bold: true, style: 'tableHeader' },
    ],
  ];
  Object.keys(ingredientData).forEach((i) => {
    const ing: HitReportIngredientDTO = ingredientData[i];
    body.push([
      { text: ing.name, alignment: 'left' },
      ing.ingredientCount,
      ing.isBuffer
        ? ing.lowPh == ing.highPh
          ? ing.lowPh
          : ing.lowPh + ' - ' + ing.highPh
        : '-',
      ing.lowConcentration == ing.highConcentration
        ? ing.lowConcentration.toFixed(3) + ' ' + ing.concentrationUnits
        : ing.lowConcentration.toFixed(3) +
          ' - ' +
          ing.highConcentration.toFixed(3) +
          ' ' +
          ing.concentrationUnits,
    ]);
  });

  return {
    style: 'tableStyle',
    table: {
      widths: ['40%', '15%', '15%', '25%'],
      headerRows: 1,
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}
